import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { flushOutbox, getPendingCount, isOnline } from '../offline/sync';

function parseSyncOptions(options) {
  if (options && typeof options === 'object' && !('nativeEvent' in options)) {
    return options;
  }
  return {};
}

export function useNetworkSync({ labels, toastSuccess, toastError } = {}) {
  const [online, setOnline] = useState(() => isOnline());
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncError, setLastSyncError] = useState('');
  const mountedRef = useRef(true);

  const refreshPending = useCallback(async () => {
    try {
      const count = await getPendingCount();
      if (mountedRef.current) setPending(count);
      return count;
    } catch {
      if (mountedRef.current) setPending(0);
      return 0;
    }
  }, []);

  const syncNow = useCallback(
    async (options = {}) => {
      const { silent = false } = parseSyncOptions(options);

      if (!isOnline()) {
        return { flushed: 0, pending, ok: true };
      }

      setSyncing(true);
      try {
        const result = await flushOutbox();
        const nextPending = await refreshPending();

        if (result.ok && result.flushed > 0) {
          setLastSyncError('');
        } else if (result.error) {
          setLastSyncError(String(result.error));
        }

        if (!silent && labels && toastSuccess && toastError) {
          if (result.skipped === 'subscription_expired') {
            toastError(labels.subscriptionWritesBlocked);
          } else if (result.ok && result.flushed > 0) {
            toastSuccess(labels.toastSyncComplete);
          } else if (result.partialFailure || (result.error && nextPending > 0)) {
            toastError(result.error || labels.toastSyncPartial);
          } else if (!result.ok && nextPending > 0) {
            toastError(result.error || labels.toastSyncFailed);
          }
        }

        return result;
      } catch (err) {
        console.error('Sync error:', err);
        const nextPending = await refreshPending();
        setLastSyncError(err.message || 'Sync failed');
        if (!silent && labels && toastError && nextPending > 0) {
          toastError(labels.toastSyncFailed);
        }
        return { flushed: 0, pending: nextPending, ok: false, error: err.message };
      } finally {
        setSyncing(false);
      }
    },
    [pending, refreshPending, labels, toastSuccess, toastError]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const tryAutoSync = async () => {
      const count = await refreshPending();
      if (!cancelled && count > 0 && isOnline()) {
        await syncNow({ silent: true });
      }
    };

    void tryAutoSync();

    const onOnline = () => {
      setOnline(true);
      void syncNow({ silent: true });
    };
    const onOffline = () => setOnline(false);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setOnline(isOnline());
        void syncNow({ silent: true });
      }
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);

    let appListener;
    if (Capacitor.isNativePlatform()) {
      setOnline(true);
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          setOnline(true);
          void syncNow({ silent: true });
        }
      }).then((handle) => {
        appListener = handle;
      });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
      appListener?.remove();
    };
  }, [refreshPending, syncNow]);

  return { online, pending, syncing, syncNow, refreshPending, lastSyncError };
}
